"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Collection, CollectionsResponse } from "@/features/collections/types";
import { useCollections } from "@/features/collections/hooks/useCollections";
import { useInfiniteGrid } from "@/hooks/useInfiniteGrid";
import { useInfiniteScrollSentinel } from "@/hooks/useInfiniteScrollSentinel";
import { axiosInstance } from "@/lib/axios";
import type { CollectionsListing } from "./useCollectionsListing";
import { shiftedLastPage, windowShift } from "./pageWindow";

/**
 * THE /collection DATA SOURCES — the legacy logic of `CollectionsGrid` and
 * `CollectionsTableView`, moved, not changed. Same hooks, same query keys,
 * same three modes:
 * - no category: `useCollections` (paged), seeded from the SSR page;
 * - category + grid: `useInfiniteGrid` on the category endpoint (infinite);
 * - category + table: the `["collections-endpoint", …]` paged query.
 * The listing hook (`useCollectionsListing`) also calls `useCollections` with
 * the same key for the result count, so this is still one request.
 */
export function useCollectionResults(listing, lang, initialData, pageSize, t0, initialNext) {
  const $ = _c(87);
  const borrow = t0 === undefined ? 0 : t0;
  const {
    page,
    view,
    filters,
    categoryEndpoint: apiEndpoint
  } = listing;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== filters || $[1] !== lang || $[2] !== page || $[3] !== pageSize) {
    t1 = {
      page,
      perPage: pageSize,
      lang,
      ...filters
    };
    $[0] = filters;
    $[1] = lang;
    $[2] = page;
    $[3] = pageSize;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = apiEndpoint ? undefined : initialData;
  const t3 = !apiEndpoint;
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      initialData: t2,
      enabled: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const listQuery = useCollections(t1, t4);
  const listMeta = listQuery.data?.data?.page;
  const shift = !apiEndpoint && borrow > 0 && borrow < pageSize ? borrow : 0;
  const hasNext = shift > 0 && listMeta != null && page < listMeta.last_page;
  const t5 = page + 1;
  let t6;
  if ($[8] !== filters || $[9] !== lang || $[10] !== pageSize || $[11] !== t5) {
    t6 = {
      page: t5,
      perPage: pageSize,
      lang,
      ...filters
    };
    $[8] = filters;
    $[9] = lang;
    $[10] = pageSize;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = apiEndpoint ? undefined : initialNext;
  let t8;
  if ($[13] !== hasNext || $[14] !== t7) {
    t8 = {
      enabled: hasNext,
      initialData: t7
    };
    $[13] = hasNext;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const nextQuery = useCollections(t6, t8);
  const usesInfinite = Boolean(apiEndpoint) && view === "grid";
  let t9;
  if ($[16] !== apiEndpoint || $[17] !== lang || $[18] !== pageSize || $[19] !== usesInfinite) {
    t9 = {
      initialData: undefined,
      apiEndpoint,
      lang,
      pageSize,
      enabled: usesInfinite
    };
    $[16] = apiEndpoint;
    $[17] = lang;
    $[18] = pageSize;
    $[19] = usesInfinite;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const infinite = useInfiniteGrid(t9);
  const t10 = usesInfinite && infinite.hasMore;
  let t11;
  if ($[21] !== infinite.isFetchingMore || $[22] !== infinite.loadMore || $[23] !== t10) {
    t11 = {
      hasMore: t10,
      isFetching: infinite.isFetchingMore,
      onLoadMore: infinite.loadMore
    };
    $[21] = infinite.isFetchingMore;
    $[22] = infinite.loadMore;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const sentinelRef = useInfiniteScrollSentinel(t11);
  let t12;
  if ($[25] !== apiEndpoint || $[26] !== page || $[27] !== pageSize) {
    t12 = ["collections-endpoint", apiEndpoint, page, pageSize];
    $[25] = apiEndpoint;
    $[26] = page;
    $[27] = pageSize;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const endpointKey = t12;
  let t13;
  if ($[29] !== apiEndpoint || $[30] !== page || $[31] !== pageSize) {
    t13 = async () => {
      const sep = apiEndpoint.includes("?") ? "&" : "?";
      const {
        data
      } = await axiosInstance.get(`${apiEndpoint}${sep}page=${page}&per_page=${pageSize}`);
      return data;
    };
    $[29] = apiEndpoint;
    $[30] = page;
    $[31] = pageSize;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = Boolean(apiEndpoint) && view === "table";
  let t15;
  if ($[33] !== endpointKey || $[34] !== t13 || $[35] !== t14) {
    t15 = {
      queryKey: endpointKey,
      queryFn: t13,
      enabled: t14,
      staleTime: 60000,
      placeholderData: _temp
    };
    $[33] = endpointKey;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const endpointQuery = useQuery(t15);
  const paged = apiEndpoint ? endpointQuery : listQuery;
  const meta = paged.data?.data?.page;
  const currentPage = meta?.current_page ?? page;
  let t16;
  if ($[37] !== paged.data?.data?.items) {
    t16 = paged.data?.data?.items ?? [];
    $[37] = paged.data?.data?.items;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const own = t16;
  let t17;
  if ($[39] !== hasNext || $[40] !== nextQuery) {
    t17 = hasNext && !nextQuery.isPlaceholderData ? nextQuery.data?.data?.items ?? [] : [];
    $[39] = hasNext;
    $[40] = nextQuery;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const nextItems = t17;
  let t18;
  if ($[42] !== currentPage || $[43] !== nextItems || $[44] !== own || $[45] !== shift) {
    t18 = shift > 0 ? windowShift(own, nextItems, currentPage, shift) : own;
    $[42] = currentPage;
    $[43] = nextItems;
    $[44] = own;
    $[45] = shift;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const shown = t18;
  let t19;
  if ($[47] !== apiEndpoint || $[48] !== filters || $[49] !== page || $[50] !== view) {
    t19 = JSON.stringify([page, view, apiEndpoint ?? null, filters]);
    $[47] = apiEndpoint;
    $[48] = filters;
    $[49] = page;
    $[50] = view;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const identity = t19;
  const [settled, setSettled] = useState(identity);
  const placeholder = paged.isPlaceholderData;
  let t20;
  let t21;
  if ($[52] !== identity || $[53] !== paged.data || $[54] !== placeholder) {
    t20 = () => {
      if (!placeholder && paged.data) {
        setSettled(identity);
      }
    };
    t21 = [placeholder, paged.data, identity];
    $[52] = identity;
    $[53] = paged.data;
    $[54] = placeholder;
    $[55] = t20;
    $[56] = t21;
  } else {
    t20 = $[55];
    t21 = $[56];
  }
  useEffect(t20, t21);
  const sizeOnly = settled === identity;
  const items = usesInfinite ? infinite.items : shown;
  let t22;
  if ($[57] !== meta || $[58] !== pageSize || $[59] !== shift) {
    t22 = shift > 0 && meta ? shiftedLastPage(meta.total, pageSize, shift, meta.last_page) : meta?.last_page ?? 1;
    $[57] = meta;
    $[58] = pageSize;
    $[59] = shift;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  const lastPage = t22;
  const t23 = usesInfinite ? infinite.isLoading : paged.isLoading;
  const t24 = usesInfinite ? infinite.isSuccess : paged.isSuccess;
  const t25 = usesInfinite ? infinite.isFetchingMore : paged.isFetching;
  const t26 = !usesInfinite && paged.isPlaceholderData && paged.isFetching && !sizeOnly;
  const t27 = !usesInfinite && paged.isPlaceholderData && !paged.isFetching && paged.failureCount > 0;
  const t28 = usesInfinite ? 1 : (currentPage - 1) * pageSize + 1 + (currentPage > 1 ? shift : 0);
  let t29;
  if ($[61] !== apiEndpoint || $[62] !== endpointKey || $[63] !== infinite || $[64] !== listQuery || $[65] !== queryClient || $[66] !== usesInfinite) {
    t29 = () => {
      if (usesInfinite) {
        return void infinite.reset();
      }
      if (apiEndpoint) {
        return void queryClient.resetQueries({
          queryKey: endpointKey
        });
      }
      return void listQuery.reset();
    };
    $[61] = apiEndpoint;
    $[62] = endpointKey;
    $[63] = infinite;
    $[64] = listQuery;
    $[65] = queryClient;
    $[66] = usesInfinite;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== infinite.hasMore || $[69] !== infinite.isError || $[70] !== infinite.isFetchingMore || $[71] !== infinite.loadMore || $[72] !== sentinelRef) {
    t30 = {
      hasMore: infinite.hasMore,
      isFetchingMore: infinite.isFetchingMore,
      isError: infinite.isError,
      loadMore: infinite.loadMore,
      sentinelRef
    };
    $[68] = infinite.hasMore;
    $[69] = infinite.isError;
    $[70] = infinite.isFetchingMore;
    $[71] = infinite.loadMore;
    $[72] = sentinelRef;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== currentPage || $[75] !== items || $[76] !== lastPage || $[77] !== t23 || $[78] !== t24 || $[79] !== t25 || $[80] !== t26 || $[81] !== t27 || $[82] !== t28 || $[83] !== t29 || $[84] !== t30 || $[85] !== usesInfinite) {
    t31 = {
      items,
      usesInfinite,
      isLoading: t23,
      isSuccess: t24,
      isFetching: t25,
      isSwitching: t26,
      staleAfterFailure: t27,
      currentPage,
      lastPage,
      firstRank: t28,
      retry: t29,
      infinite: t30
    };
    $[74] = currentPage;
    $[75] = items;
    $[76] = lastPage;
    $[77] = t23;
    $[78] = t24;
    $[79] = t25;
    $[80] = t26;
    $[81] = t27;
    $[82] = t28;
    $[83] = t29;
    $[84] = t30;
    $[85] = usesInfinite;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  return t31;
}
function _temp(previous) {
  return previous;
}